const KEY_PREFIX = 'scaleDraft:'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

function getUserId() {
    return String(localStorage.getItem('user_id') || '').trim()
}

function buildKey(scaleId, userId) {
    const id = String(userId || getUserId() || '').trim()
    const scale = String(scaleId || '').trim()
    if (!id || !scale) return ''
    return `${KEY_PREFIX}${id}:${scale}`
}

function readStorage(key) {
    if (!key) return null
    try {
        return localStorage.getItem(key)
    } catch (error) {
        return null
    }
}

function writeStorage(key, value) {
    if (!key) return
    try {
        localStorage.setItem(key, value)
    } catch (error) {
        // 隐私模式或配额不足时忽略，不影响答题
    }
}

function removeStorage(key) {
    if (!key) return
    try {
        localStorage.removeItem(key)
    } catch (error) {
        // 忽略本地存储异常
    }
}

export function isEmptyAnswer(value) {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && !value.length)
}

export function hasFilledAnswers(answers) {
    if (!answers || typeof answers !== 'object') return false
    return Object.keys(answers).some((key) => !isEmptyAnswer(answers[key]))
}

function pickCachedValue(cachedAnswers, questionId) {
    if (!cachedAnswers || typeof cachedAnswers !== 'object') return undefined
    if (Object.prototype.hasOwnProperty.call(cachedAnswers, questionId)) return cachedAnswers[questionId]
    const asString = String(questionId)
    if (Object.prototype.hasOwnProperty.call(cachedAnswers, asString)) return cachedAnswers[asString]
    return undefined
}

function matchOptionId(options, value) {
    const list = Array.isArray(options) ? options : []
    const matched = list.find((option) => option && (option.id === value || String(option.id) === String(value)))
    return matched ? matched.id : undefined
}

function sanitizeValue(question, value) {
    if (!question) return undefined
    if (question.type === '单选题') {
        if (isEmptyAnswer(value)) return ''
        const optionId = matchOptionId(question.options, value)
        return optionId === undefined ? undefined : optionId
    }
    if (question.type === '多选题') {
        if (!Array.isArray(value)) return undefined
        return value
            .map((item) => matchOptionId(question.options, item))
            .filter((item) => item !== undefined)
    }
    if (question.type === '数字输入') {
        if (isEmptyAnswer(value)) return ''
        if (!Number.isFinite(Number(value))) return undefined
        return String(value)
    }
    if (question.type === '文本输入') {
        if (value === null || value === undefined) return ''
        return String(value)
    }
    if (question.type === '时间选择') {
        if (isEmptyAnswer(value)) return ''
        const text = String(value)
        return /^\d{2}:\d{2}$/.test(text) ? text : undefined
    }
    return undefined
}

export function mergeDraftAnswers(questions, cachedAnswers) {
    const result = {}
    const list = Array.isArray(questions) ? questions : []
    list.forEach((question) => {
        if (!question || question.id === undefined || question.id === null) return
        const cachedValue = pickCachedValue(cachedAnswers, question.id)
        if (cachedValue === undefined) return
        const cleaned = sanitizeValue(question, cachedValue)
        if (cleaned !== undefined) result[question.id] = cleaned
    })
    return result
}

export function loadScaleDraft(scaleId) {
    const userId = getUserId()
    const key = buildKey(scaleId, userId)
    const raw = readStorage(key)
    if (!raw) return null
    try {
        const draft = JSON.parse(raw)
        const cachedAt = Number(draft && draft.updatedAt)
        if (!draft || draft.userId !== userId || String(draft.scaleId) !== String(scaleId) || !Number.isFinite(cachedAt)) {
            removeStorage(key)
            return null
        }
        if (Date.now() - cachedAt > TTL_MS) {
            removeStorage(key)
            return null
        }
        if (!hasFilledAnswers(draft.answers)) {
            removeStorage(key)
            return null
        }
        return draft
    } catch (error) {
        removeStorage(key)
        return null
    }
}

export function saveScaleDraft(scaleId, answers) {
    const userId = getUserId()
    const key = buildKey(scaleId, userId)
    if (!key) return
    if (!hasFilledAnswers(answers)) {
        removeStorage(key)
        return
    }
    writeStorage(key, JSON.stringify({
        userId,
        scaleId: String(scaleId),
        answers,
        updatedAt: Date.now(),
    }))
}

export function removeScaleDraft(scaleId) {
    removeStorage(buildKey(scaleId, getUserId()))
}

export function hasScaleDraft(scaleId) {
    return Boolean(loadScaleDraft(scaleId))
}

export function clearUserScaleDrafts(userId) {
    const id = String(userId || getUserId() || '').trim()
    if (!id) return
    const prefix = `${KEY_PREFIX}${id}:`
    const keys = []
    try {
        for (let index = 0; index < localStorage.length; index += 1) {
            const key = localStorage.key(index)
            if (key && key.indexOf(prefix) === 0) keys.push(key)
        }
        keys.forEach((key) => localStorage.removeItem(key))
    } catch (error) {
        // 忽略本地存储异常
    }
}
